/**
 * A work session lasts one local calendar day.
 *
 * 음성공장 runs no night shift, so a session that began on an earlier local day is finished
 * however recently it was used: a floor PC left signed in overnight must not let the first
 * person in tomorrow act as yesterday's account.
 *
 * The rule is deliberately a question about the clock at the moment it is asked, never a
 * countdown. A countdown started the evening before does not survive a suspended laptop, and a
 * timer that fires late would otherwise be what decides the answer.
 */

/** Start of the local day the given instant falls in. */
function startOfLocalDay(at: number) {
  const day = new Date(at);
  day.setHours(0, 0, 0, 0);
  return day.getTime();
}

/**
 * The Firebase `auth_time` claim (seconds since the epoch) as milliseconds. `null` when the
 * claim is missing or unreadable - an unknown start is never treated as a recent one.
 */
export function identitySessionStartedAt(authTime: unknown): number | null {
  const startedAt = Number(authTime) * 1_000;
  return Number.isFinite(startedAt) && startedAt > 0 ? startedAt : null;
}

/**
 * Whether the session that began at `startedAt` is over. Answered from `now` alone, so the same
 * session is refused after midnight and accepted before it, no matter how long it has run.
 */
export function identitySessionExpired(startedAt: number | null, now = Date.now()): boolean {
  if (startedAt === null || !Number.isFinite(startedAt)) return true;
  return startedAt < startOfLocalDay(now);
}

/** How long until the next local midnight, for re-asking the question above. */
export function millisecondsUntilNextLocalDay(now = Date.now()): number {
  const next = new Date(now);
  next.setHours(24, 0, 0, 0);
  return Math.max(next.getTime() - now, 0);
}

export function replaceIdentityExpiryTimer(
  timer: ReturnType<typeof setTimeout> | undefined,
  delay: number,
  expire: () => void,
) {
  if (timer) clearTimeout(timer);
  return setTimeout(expire, delay);
}
